import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { isApiError } from '@/lib/api/client';
import { registrationApi } from './registration.api';
import { registrationKeys } from './registration.queries';
import type {
  CreateRegistrationRequest,
  UpdateRegistrationRequest,
  RegistrationsListParams,
  AdvanceStageRequest
} from '../types';

export function useCreateRegistrationMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('registration');

  return useMutation({
    mutationFn: (data: CreateRegistrationRequest) =>
      registrationApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.metrics()
      });
    }
  });
}

export function useUpdateRegistrationMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('registration');

  return useMutation({
    mutationFn: ({
      id,
      data
    }: {
      id: number;
      data: UpdateRegistrationRequest;
    }) => registrationApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.details()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.metrics()
      });
    }
  });
}

export function useDeleteRegistrationMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('registration');

  return useMutation({
    mutationFn: (id: number) => registrationApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.metrics()
      });
    }
  });
}

export function useAdvanceStageMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('registration');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AdvanceStageRequest }) =>
      registrationApi.advanceStage(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('stageAdvancedSuccess')
    },
    onError: (error) => {
      // 409 `unexpected_stage`: the container already moved elsewhere. The
      // global handler toasts the server's message; refetch so the row shows
      // where it really is.
      if (isApiError(error) && error.status === 409) {
        queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: registrationKeys.all
      });
    }
  });
}

export function useBulkDeleteRegistrationsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('registration');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<RegistrationsListParams> }
    ) => registrationApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: registrationKeys.lists()
      });
      queryClient.invalidateQueries({
        queryKey: registrationKeys.metrics()
      });
    }
  });
}
